import { DateInput } from '../ui/Form';

/** The Indian financial year containing a date: 1 April to 31 March. */
export function financialYear(on = new Date()): { from: string; to: string } {
  const start = on.getMonth() >= 3 ? on.getFullYear() : on.getFullYear() - 1;
  return { from: `${start}-04-01`, to: `${start + 1}-03-31` };
}

function monthRange(offset = 0): { from: string; to: string } {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
  const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
  const iso = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(first), to: iso(last) };
}

/** A single day, a number of days back from today. */
function dayRange(back = 0): { from: string; to: string } {
  const d = new Date();
  d.setDate(d.getDate() - back);
  const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: day, to: day };
}

/**
 * The period every account screen is read for.
 *
 * Two bare date boxes with no labels is the state this was in — a person had to
 * guess which was the start and which the end, and both were empty, so the
 * ledger opened on nothing. Here they are labelled, they open on the current
 * financial year, and the four periods a shop actually asks for are one click.
 */
export function PeriodBar({
  from,
  to,
  onChange,
  children,
}: {
  from: string;
  to: string;
  onChange: (range: { from: string; to: string }) => void;
  children?: React.ReactNode;
}) {
  const fy = financialYear();
  const lastFy = { from: `${Number(fy.from.slice(0, 4)) - 1}-04-01`, to: `${Number(fy.to.slice(0, 4)) - 1}-03-31` };

  const quick: { label: string; range: { from: string; to: string } }[] = [
    { label: 'Today', range: dayRange(0) },
    { label: 'Yesterday', range: dayRange(1) },
    { label: 'This month', range: monthRange(0) },
    { label: 'Last month', range: monthRange(-1) },
    { label: 'This year', range: fy },
    { label: 'Last year', range: lastFy },
  ];

  const isOn = (r: { from: string; to: string }): boolean => r.from === from && r.to === to;

  return (
    <div className="no-print mb-4 flex flex-wrap items-end gap-3 rounded-lg border border-silver-100 bg-surface px-3 py-2.5 shadow-card">
      {children}
      <div>
        <p className="label">From</p>
        <DateInput value={from} onChange={(v) => onChange({ from: v, to })} allowFuture />
      </div>
      <div>
        <p className="label">To</p>
        <DateInput value={to} onChange={(v) => onChange({ from, to: v })} allowFuture />
      </div>
      <div className="flex flex-wrap gap-1 pb-1">
        {quick.map((q) => (
          <button
            key={q.label}
            onClick={() => onChange(q.range)}
            className={`rounded-full px-2.5 py-1 text-2xs font-medium ${
              isOn(q.range) ? 'bg-velvet text-white' : 'bg-silver-50 text-ink-soft hover:bg-gold-50'
            }`}
          >
            {q.label}
          </button>
        ))}
      </div>
    </div>
  );
}
